/**
 * Runs one table of the spec, or every table of the tab book, through the
 * query queue: the table's banner once per session, then the table's script
 * as one request, then the table built from its outputs. Shared by the
 * preview (one table) and the run dialog (the book).
 */

import type { StoreApi } from "zustand"
import type { EngineOutputItem, EngineQueryResult } from "types/engineOutput.types"
import { TRACE_CHECK } from "features/Provenance/utils/traceTable"
import {
  QueryQueue,
  SupersededRunError,
} from "features/Dashboard/runtime/queryQueue"
import { canonicalJson } from "../model/hash"
import { buildResult } from "./buildResult"
import { ENGINE_TIMEOUT, EngineTimeoutError } from "./engineTimeout"
import {
  BookRun,
  FINISHED,
  planKey,
  selectDisplayStatus,
  selectPlan,
  selectPlanKey,
  TableRun,
  TabSpecState,
} from "./tabSpecStore"

export { planKey }

const message = (e: unknown): string =>
  e instanceof Error ? e.message : "The table could not be run."

/**
 * Removes names one request each; a name that is not there is not an error
 * here. Stops once the session has moved on: the names are not its own.
 */
const removeEach = async (
  queue: QueryQueue,
  names: string[],
  moved: () => boolean
): Promise<void> => {
  for (const name of names) {
    if (moved()) return
    try {
      await queue.runPlain(`REMOVE ${name}`)
    } catch {
      // The name was never created, or is already gone.
    }
  }
}

/** What the engine answers once the session it names has been reaped. */
const SESSION_GONE = /Cannot find id/i
/** The message of a table whose session the engine no longer has. */
export const SESSION_ENDED =
  "The data session has ended. Choose Restart session and run the table again."
/** Why a book stopped at a table whose request got no answer in time. */
export const ENGINE_STOPPED = "The engine stopped answering. Restart the session and run again."

const idleRun = (): TableRun => ({ status: "idle", key: "", notes: [], warnings: [] })

/**
 * The banner builds of each queue by banner name, for the life of the queue.
 * Every table that needs a banner waits for the outcome of its build before
 * it sends its own script: the queue is serial, so a script queued behind a
 * build that fails would run against a banner that is missing or half built.
 * The known banners in the store are what the screens read.
 */
const bannerBuilds = new WeakMap<QueryQueue, Map<string, Promise<void>>>()

const buildsOf = (queue: QueryQueue): Map<string, Promise<void>> => {
  const found = bannerBuilds.get(queue)
  if (found) return found
  const made = new Map<string, Promise<void>>()
  bannerBuilds.set(queue, made)
  return made
}

/**
 * Plans and runs one table, and resolves once its result, its error or its
 * skip is in the store. Nothing runs unless the session is ready and the
 * catalogue is in. Once the session has moved on (a new generation) the run
 * sends and writes nothing more; a run that ends without a result, because
 * it was superseded, goes back to idle. A run writes its end (done, error or
 * idle) only while the store still holds its own `running` entry: a later
 * run of the same table that started meanwhile owns the table.
 */
export const runTable = async (
  store: StoreApi<TabSpecState>,
  queue: QueryQueue,
  tableId: string
): Promise<void> => {
  const state = store.getState()
  if (state.session.status !== "ready" || !state.catalogue) return
  // The plan the screens show, compiled once for them all.
  const plan = selectPlan(state, tableId)
  if (!plan) return
  const generation = state.session.generation
  const moved = (): boolean => store.getState().session.generation !== generation
  const key = selectPlanKey(state, tableId) ?? planKey(plan, generation)
  const base = { key, plan, notes: [] as string[], warnings: plan.warnings }

  if (plan.status === "skip") {
    state.setRun(tableId, { ...base, status: "skipped", message: plan.skipReason })
    return
  }
  const running: TableRun = { ...base, status: "running" }
  state.setRun(tableId, running)
  /**
   * Ends a run that will not finish. Only while the store still holds this
   * call's run: a new session has already cleared it, and another call for
   * the same table owns its own.
   */
  const isCurrent = (): boolean => store.getState().runs[tableId] === running
  const abandon = (): void => {
    if (isCurrent()) store.getState().setRun(tableId, idleRun())
  }
  /** Ends the run with an error, only while it is still the table's current run. */
  const fail = (text: string): void => {
    if (!isCurrent()) return
    store.getState().setRun(tableId, {
      ...base,
      status: "error",
      message: SESSION_GONE.test(text) ? SESSION_ENDED : text,
    })
  }

  const banner = plan.banner
  if (banner?.script) {
    const builds = buildsOf(queue)
    // A known banner is built, or being built by another table: wait for
    // that build. A banner known without a build on this queue was built
    // before it: go on at once.
    const starts = !store.getState().session.knownBanners.has(banner.name)
    let build = starts ? undefined : builds.get(banner.name)
    if (starts) {
      // Recorded before it is sent, so a table that needs the same banner
      // meanwhile waits for this build instead of sending it again.
      store.getState().addKnownBanner(banner.name)
      build = queue.runPlain(banner.script).then(() => undefined)
      builds.set(banner.name, build)
    }
    if (build) {
      try {
        await build
      } catch (e) {
        // Only the table that started the build undoes it, once for all
        // the tables that waited for it; a later run can try again.
        if (starts && builds.get(banner.name) === build) builds.delete(banner.name)
        if (moved()) {
          abandon()
          return
        }
        if (starts) store.getState().forgetKnownBanner(banner.name)
        if (e instanceof SupersededRunError) {
          abandon()
          return
        }
        // After a timeout a REMOVE would wait as long; the next RESET removes the name.
        const timedOut = e instanceof EngineTimeoutError
        if (starts && !timedOut) {
          // A banner that failed half way would refuse to be created again.
          await removeEach(queue, [banner.name], moved)
          if (moved()) {
            abandon()
            return
          }
        }
        fail(timedOut ? ENGINE_TIMEOUT : `The banner could not be built: ${message(e)}`)
        return
      }
      if (moved()) {
        abandon()
        return
      }
    }
  }

  let result: EngineQueryResult
  try {
    result = await queue.enqueue(plan.script)
  } catch (e) {
    if (moved() || e instanceof SupersededRunError) {
      abandon()
      return
    }
    // The engine stops at the failing line and keeps what the lines before
    // it created, so the script's own REMOVE lines did not run. After a
    // timeout the REMOVEs would wait as long: the next RESET removes the names.
    if (!(e instanceof EngineTimeoutError)) {
      await removeEach(queue, plan.temps, moved)
      if (moved()) {
        abandon()
        return
      }
    }
    fail(message(e))
    return
  }
  if (moved()) {
    abandon()
    return
  }
  // A later run of the table (after an edit) that landed first owns the
  // table now: this one writes nothing over it.
  if (!isCurrent()) return
  try {
    const built = buildResult(plan, result.result?.output ?? [])
    store.getState().setRun(tableId, {
      ...base,
      status: "done",
      model: built.model,
      notes: built.notes,
      // The plan's sentences, then what the run found (the check's outcome).
      warnings: [...plan.warnings, ...built.warnings],
    })
  } catch (e) {
    // A ResultError: the script ran to its end, its REMOVE lines included,
    // so there is nothing to clean up.
    fail(message(e))
  }
}

/**
 * Runs the check a trace of a cell sends (the table's script with a check
 * table added after the table's line, see the trace panel) in turn with the
 * queued scripts, and gives its output. The engine stops at a failing line
 * and keeps what the lines before it created, so after a failure the
 * table's temporaries `temps` and the check table are removed, as after a
 * failed run of the table. After a timeout the REMOVEs would wait as long:
 * the next RESET removes the names.
 */
export const runTraceCheck = async (
  queue: QueryQueue,
  temps: string[],
  script: string
): Promise<EngineOutputItem[]> => {
  try {
    const result = await queue.runPlain(script)
    return result.result?.output ?? []
  } catch (e) {
    if (!(e instanceof EngineTimeoutError) && !(e instanceof SupersededRunError))
      await removeEach(queue, [TRACE_CHECK, ...temps], () => false)
    if (e instanceof Error && SESSION_GONE.test(e.message)) throw new Error(SESSION_ENDED)
    throw e
  }
}

/**
 * Runs every included table that is not already done, one after another,
 * and keeps `bookRun` up to date. A failed table does not stop the book.
 * Cancelling stops before the next table; the one running finishes. The book
 * stops by itself (as cancelled) when the session is not ready, when a new
 * session has begun, when a table finds the session gone, or when a table's
 * request got no answer in time (then with `ENGINE_STOPPED` as its reason:
 * every later table would wait as long).
 * `done` counts the tables with a finished run of the book's session.
 *
 * Only one book runs at a time: a call while `bookRun` is active starts
 * nothing. A book owns `bookRun` by identity: the object it wrote last, or
 * the cancelled copy `cancelBook` put in its place. Once `bookRun` is
 * anything else (another book, a reset), the book stops and writes nothing
 * more, into `bookRun` or into the runs. Whatever happens after it has
 * started, even an exception, it ends with `active: false` while it is still
 * its own; the exception then reaches the caller.
 */
export const runBook = async (
  store: StoreApi<TabSpecState>,
  queue: QueryQueue
): Promise<void> => {
  if (store.getState().bookRun?.active) return
  const generation = store.getState().session.generation
  const ids = store
    .getState()
    .document.tables.filter((t) => t.include)
    .map((t) => t.id)
  const progress: BookRun = {
    total: ids.length,
    done: 0,
    failed: 0,
    skipped: 0,
    cancelled: false,
    active: true,
  }
  let stopped = false
  let reason: string | undefined
  /** The `bookRun` of this book: the object it wrote last, or the cancelled copy it adopted. */
  let own: BookRun = { ...progress }
  const isOwn = (): boolean => store.getState().bookRun === own
  // `cancelBook` replaces this book's object with a cancelled copy of it. The
  // copy is adopted at the moment it replaces this book's own object, so it
  // stays this book's by identity; any other replacement ends the ownership.
  const unsubscribe = store.subscribe((next, prev) => {
    const copy = next.bookRun
    if (
      prev.bookRun === own &&
      copy !== own &&
      copy !== null &&
      canonicalJson(copy) === canonicalJson({ ...own, cancelled: true })
    )
      own = copy
  })
  const publish = (active: boolean): void => {
    if (!isOwn()) return
    own = {
      ...progress,
      cancelled: stopped || own.cancelled,
      active,
      ...(reason ? { reason } : {}),
    }
    store.getState().setBookRun(own)
  }
  store.getState().setBookRun(own)
  try {
    ids.forEach((id) => {
      const run = store.getState().runs[id]
      if (!run || run.status === "idle")
        store.getState().setRun(id, { status: "queued", key: "", notes: [], warnings: [] })
    })
    for (const id of ids) {
      if (!isOwn() || own.cancelled) break
      const { session } = store.getState()
      if (session.status !== "ready" || session.generation !== generation) {
        stopped = true
        break
      }
      if (selectDisplayStatus(store.getState(), id) !== "done")
        await runTable(store, queue, id)
      const now = store.getState()
      const run = now.runs[id]
      if (now.session.generation === generation && run && FINISHED.includes(run.status)) {
        progress.done++
        if (run.status === "error") progress.failed++
        else if (run.status === "skipped") progress.skipped++
      }
      publish(true)
      // Every later table would fail the same way, or wait as long.
      if (run?.status === "error" && run.message === SESSION_ENDED) {
        stopped = true
        break
      }
      if (run?.status === "error" && run.message === ENGINE_TIMEOUT) {
        stopped = true
        reason = ENGINE_STOPPED
        break
      }
    }
  } finally {
    unsubscribe()
    // A table the book did not get to no longer waits. After a new session
    // began, the runs are that session's, and after another book took over
    // they are that book's: both are left alone.
    if (isOwn() && store.getState().session.generation === generation)
      ids.forEach((id) => {
        if (store.getState().runs[id]?.status === "queued")
          store.getState().setRun(id, idleRun())
      })
    publish(false)
  }
}
